import { AfterViewInit, Component, ElementRef, Input, ViewChild, ViewContainerRef } from '@angular/core';

import { firstValueFrom, Subject } from 'rxjs';

import { TwcPopupService, TwcTrowserComponent } from '@topcon/twc-angular';

import { CollageCloudService, MagnetService } from '@mirage-projects/cw-commons';
import { PcvizCollageWebFileBrowserListResult, ReportVolume, VolumeReportFormatted } from '@mirage-projects/cw-models';

import { VolumeReportService } from './volume-report.service';

/**
 * Component to show and export the layer volume data
 */
@Component({
    selector: 'cw-volume-report',
    templateUrl: 'volume-report.component.html',
    styleUrls: ['./volume-report.component.scss'],
    standalone: false,
})
export class VolumeReportComponent implements AfterViewInit {
    loading = true;
    report: Partial<ReportVolume> | undefined;
    reportFormatted!: VolumeReportFormatted;
    volumeScreenshotUrl?: string;
    pointCloudScreenshotUrl?: string;

    private initCompleteSubject$ = new Subject<void>();
    initComplete$ = this.initCompleteSubject$.asObservable();

    /** Id of the volume layer */
    @Input() layerId!: string;
    /** Project name to add to the report */
    @Input() projectName!: string;
    /** Filebrowser result of the volume screenshot */
    @Input() volumeScreenshot?: PcvizCollageWebFileBrowserListResult;
    /** Filebrowser result of the pointcloud screenshot */
    @Input() pointCloudScreenshot?: PcvizCollageWebFileBrowserListResult;
    /** Flag to hide component items if rendered to be downloaded */
    @Input() isDownload = false;

    @ViewChild(TwcTrowserComponent) trowser!: TwcTrowserComponent;
    @ViewChild('reportInit', { read: ElementRef }) reportInit!: ElementRef;

    constructor(
        private volumeReportService: VolumeReportService,
        private popupService: TwcPopupService,
        private collageCloudService: CollageCloudService,
        private magnetService: MagnetService,
        private viewContainerRef: ViewContainerRef
    ) {}

    async ngAfterViewInit(): Promise<void> {
        if (this.isSite && this.volumeScreenshot) {
            const url = await firstValueFrom(this.collageCloudService.getRedirectURL(this.volumeScreenshot.href));
            this.volumeScreenshotUrl = url;
        }
        if (this.isSite && this.pointCloudScreenshot) {
            const url = await firstValueFrom(this.collageCloudService.getRedirectURL(this.pointCloudScreenshot.href));
            this.pointCloudScreenshotUrl = url;
        }

        this.report = await this.volumeReportService.loadReportData(this.layerId);
        if (this.report) {
            const reportFormatted = this.volumeReportService.addUnitsToReport(this.report);
            this.reportFormatted = this.volumeReportService.translateReportStringValues(reportFormatted);
        } else {
            this.popupService.hideAll();
        }

        this.loading = false;
        this.initCompleteSubject$.next();
    }

    /**
     * When dowload button is clicked, it should download a file with the report data text
     */
    onDownloadText(): void {
        this.volumeReportService.downloadTextReport(this.reportFormatted, this.projectName);
    }

    /**
     * When dowload button is clicked, it should download a file with the report data as csv
     */
    onDownloadCsv(): void {
        this.volumeReportService.downloadCsvReport(this.reportFormatted, this.projectName);
    }

    /**
     * When dowload button is clicked, it should download a file with the report data as html
     */
    async onDownloadHTML(): Promise<void> {
        const { layerId, projectName, volumeScreenshot, pointCloudScreenshot } = this;
        const isDownload = true;
        await this.volumeReportService.downloadHtmlReport(
            { layerId, projectName, volumeScreenshot, pointCloudScreenshot, isDownload },
            this.viewContainerRef
        );
    }

    /* istanbul ignore next */
    get isSite(): boolean {
        return this.magnetService.isSite();
    }

    get hasScreenshotImages(): boolean {
        return !!(this.volumeScreenshotUrl || this.pointCloudScreenshotUrl);
    }
}
